import type {
  Channel, Program, MediaAsset, ScheduleGrid, ScheduleItem,
  LiveSource, InterludeClip,
  RecurrenceRule, CreateRecurrencePayload,
  AiSuggestion, AiSuggestedProgram, AiSuggestedAd,
  Collection, Series, ProgramCategory, ProgramGenre, BulkProgramRequest, BulkProgramResult, AntennaElementType, CgAsset, CgFont, CgOverlay, CgOverlayUpcomingPassage, CgDesignerTemplatePayload, CgDesignerTemplateResult, CgTemplate, LogoProfile,
  TemplateSlot, TemplateProposal, TemplateRetimeResult, GridGeneration, GenerationScope, GenerationDestination,
  RebroadcastReport, RebroadcastRequest,
  ReplaceProgramRangeResult, ReplaceProgramScope,
  RedundancyConfigResponse, PlayoutTarget, VideoRouter,
  AssetFamily, AssetPresenceEntry, AssetPresenceScanReport, AssetPresenceStatus,
  AssetPurgeResult, DataHealthSummary,
  ClockBootstrap, ClockDisplay, ClockDisplayKind, ClockDisplaySecret, ClockLayoutConfig, ClockTemplate, ClockTheme,
} from '../types/domain.js'
import { API_BASE_URL as BASE_URL, HttpError, httpRequest } from './http.js'

// ─── Configuration ────────────────────────────────────────────────────────────

// In Docker development and production, serve the SPA and API from the same
// origin. This prevents a dev UI on :3001 from accidentally querying :3000.
// ─── Fetch de base avec gestion d'erreurs ─────────────────────────────────────

export const ApiError = HttpError

async function apiFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  return httpRequest<T>(path, options)
}

const get   = <T>(path: string)               => apiFetch<T>(path)
const post  = <T>(path: string, body?: unknown) => apiFetch<T>(path, { method: 'POST',   body: body ? JSON.stringify(body) : undefined })
const patch = <T>(path: string, body: unknown)  => apiFetch<T>(path, { method: 'PATCH',  body: JSON.stringify(body) })
const put   = <T>(path: string, body: unknown)  => apiFetch<T>(path, { method: 'PUT',    body: JSON.stringify(body) })
const del   = <T>(path: string)               => apiFetch<T>(path, { method: 'DELETE' })

function toQS(params: Record<string, unknown>): string {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
  if (!entries.length) return ''
  return '?' + entries.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`).join('&')
}

// ─── Clock ──────────────────────────────────────────────────────────────────

export const clockApi = {
  templates: (channelId: string) => get<ClockTemplate[]>(`/clock-templates${toQS({ channelId })}`),
  template: (id: string) => get<ClockTemplate>(`/clock-templates/${id}`),
  createTemplate: (data: { channelId: string; name: string; description?: string; displayKind: ClockDisplayKind; defaultTheme: ClockTheme; draftConfig?: ClockLayoutConfig }) => post<ClockTemplate>('/clock-templates', data),
  saveDraft: (id: string, data: Partial<Pick<ClockTemplate, 'name' | 'description' | 'displayKind' | 'defaultTheme' | 'draftConfig'>>) => patch<ClockTemplate>(`/clock-templates/${id}/draft`, data),
  publish: (id: string) => post<ClockTemplate>(`/clock-templates/${id}/publish`),
  duplicate: (id: string) => post<ClockTemplate>(`/clock-templates/${id}/duplicate`),
  deleteTemplate: (id: string) => del<void>(`/clock-templates/${id}`),
  displays: (channelId: string) => get<ClockDisplay[]>(`/clock-displays${toQS({ channelId })}`),
  createDisplay: (data: { channelId: string; templateId: string; name: string; locationLabel?: string; displayKind: ClockDisplayKind; themeOverride?: ClockTheme | null; audibleAlerts?: boolean }) => post<ClockDisplaySecret>('/clock-displays', data),
  updateDisplay: (id: string, data: Partial<ClockDisplay>) => patch<ClockDisplay>(`/clock-displays/${id}`, data),
  deleteDisplay: (id: string) => del<void>(`/clock-displays/${id}`),
  rotateToken: (id: string) => post<ClockDisplaySecret>(`/clock-displays/${id}/rotate-token`),
  /**
   * Relit le lien d'accès d'un écran. En POST bien qu'il n'écrive rien : la
   * réponse porte un secret, et un GET finirait dans les journaux d'accès du
   * proxy et l'historique du navigateur.
   */
  launchUrl: (id: string) => post<{ displayId: string; launchUrl: string }>(`/clock-displays/${id}/launch-url`),
}

async function clockViewFetch<T>(displayId: string, token: string, suffix: string): Promise<T> {
  const response = await fetch(`${BASE_URL}/clock-view/${encodeURIComponent(displayId)}/${suffix}`, {
    cache: 'no-store', credentials: 'include', headers: { Authorization: `Clock ${token}` },
  })
  if (!response.ok) {
    const body = await response.json().catch(() => ({})) as { error?: string; message?: string }
    throw new HttpError(response.status, body.error ?? 'CLOCK_UNAVAILABLE', body.message ?? `HTTP ${response.status}`)
  }
  return response.json() as Promise<T>
}

export const clockViewApi = {
  bootstrap: (displayId: string, token: string) => clockViewFetch<ClockBootstrap>(displayId, token, 'bootstrap'),
  time: (displayId: string, token: string, clientSentAt: number) => clockViewFetch<{ clientSentAt: string | null; serverTime: string }>(displayId, token, `time?clientSentAt=${clientSentAt}`),
}

// ─── Programs ─────────────────────────────────────────────────────────────────

export interface RelatedEdition {
  id: string
  title: string
  durationSec: number
  isNew: boolean
  plannedFirstAirDate: string | null
  firstAiredAt: string | null
  legacyRerunCount: number
  programCode: string | null
  series: { id: string; title: string } | null
  collections: Array<{ id: string; name: string; color: string | null }>
  media: { id: string; fileName: string; status: string; durationSec: number | null } | null
  hasUsableMedia: boolean
}

export interface RelatedEditionsResult {
  source: {
    id: string
    title: string
    series: { id: string; title: string } | null
    collections: Array<{ id: string; name: string; color: string | null }>
  }
  programs: RelatedEdition[]
}

export interface ProgramMediaSuggestion {
  programId: string
  partNumber: number
  expectedStem: string | null
  matches: Array<{ filePath: string; fileName: string }>
}

/** Aperçu d'une entrée de la file « À traiter » : de quoi remplir une carte. */
export interface WorkBucket {
  count:  number
  sample: Array<{ id: string; title: string }>
}

export interface CatalogOverviewResponse {
  programCount:      number
  totalDurationSec:  number
  collectionCount:   number
  seriesCount:       number
  mediaCount:        number
  mediaNeedingCheck: number
  work:              Record<'no-media' | 'missing-file' | 'no-branding' | 'missing-epg', WorkBucket>
  workTotal:         number
}

/** Alerte de livraison portée par un inédit à venir. Miroir du type serveur
 *  `lib/premiere-alerts.ts` — même vocabulaire, mêmes règles. */
export type PremiereAlertKind = 'NOT_SCHEDULED' | 'MEDIA_MISSING' | 'EPG_INCOMPLETE' | 'RIGHTS_EXPIRED'

export interface PremiereCalendarEntry {
  programId:      string
  title:          string
  /** Journée d'antenne `AAAA-MM-JJ` : la case du calendrier. */
  day:            string
  /** Heure d'antenne, seulement si l'inédit est déposé sur une grille. */
  startTime:      string | null
  itemId:         string | null
  scheduled:      boolean
  durationSec:    number
  isLive:         boolean
  seriesId:       string | null
  seriesTitle:    string | null
  categoryLabel:  string | null
  categoryColor:  string | null
  alerts:         PremiereAlertKind[]
}

export interface PremiereCalendarResult {
  from:    string
  to:      string
  entries: PremiereCalendarEntry[]
}

/** Constat du décodage réel de la piste audio. Miroir de `AudioCheckSummary`. */
export interface AudioCheckSummary {
  severity:        'OK' | 'WARNING' | 'ERROR'
  issues:          string[]
  codec:           string | null
  sampleRate:      number | null
  channels:        number | null
  /** Trames refusées par le décodeur : autant de claquements à l'antenne. */
  decodeErrors:    number
  decoderMessages: string[]
  maxVolumeDb:     number | null
  meanVolumeDb:    number | null
  truncated:       boolean
}

/** Réponse de `POST /media/:id/check`. `issues` cumule vidéo, durée et audio. */
export interface MediaCheckResult {
  status:   string
  issues:   string[]
  audio:    AudioCheckSummary | null
  metadata: Record<string, unknown>
}

/** Une entrée de la colonne « Affiner », avec le nombre de fiches qu'elle donne. */
export interface CatalogFacets {
  total: number
  state: {
    all:         number
    incomplete:  number
    /** Incomplets posés sur la journée d'antenne en cours. */
    incompleteToday: number
    missingFile: number
    genreless:   number
  }
  media: {
    available: number
    none:      number
  }
  /** Programmes en direct, toutes facettes d'état confondues. */
  live: number
  /** Périmètre d'archivage, compté hors filtre `archived` en cours. */
  archive: {
    active:   number
    archived: number
    all:      number
  }
  genres: { id: string; label: string; color: string | null; sortOrder: number; count: number }[]
}

export interface ProgramPartsRemovalPreview {
  removedPartIds: string[]
  retainedAirings: number
  token: string
  airings: Array<{
    id: string; gridId: string; gridName: string; programId: string; title: string
    startTime: string; endTime: string; durationSec: number
  }>
}

export const programsApi = {
  list:       (q: any = {})                          => get<any>(`/programs${toQS(q)}`),
  catalogOverview: (q: any = {})                     => get<CatalogOverviewResponse>(`/programs/catalog-overview${toQS(q)}`),
  /** Facettes « Affiner » du catalogue, chacune avec son compte. */
  catalogFacets: (q: Record<string, unknown> = {})    => get<CatalogFacets>(`/programs/catalog-facets${toQS(q)}`),
  /** Calendrier des prochains inédits (direction de production). Bornes = journées
   *  d'antenne incluses, au format `AAAA-MM-JJ`. */
  premiereCalendar: (q: { channelId: string; from: string; to: string }) =>
    get<PremiereCalendarResult>(`/programs/premiere-calendar${toQS(q)}`),
  getById:    (id: string)                                    => get<Program>(`/programs/${id}`),
  create:     (data: any)                    => post<Program>('/programs', data),
  update:     (id: string, data: any)        => patch<Program>(`/programs/${id}`, data),
  updateParts:(id: string, data: { parts: Array<{
    id?: string
    title?: string | null
    titleShort?: string | null
    epgSubtitle?: string | null
    summary?: string | null
    tags?: string[] | null
    thumbnailUrl?: string | null
    durationSec: number
  }>; interPartBreakSec: number; removalPreviewToken?: string }) =>
    put<Program>(`/programs/${id}/parts`, data),
  previewPartsRemoval: (id: string, data: { parts: Array<{ id: string; durationSec: number }>; interPartBreakSec: number }) =>
    put<ProgramPartsRemovalPreview>(`/programs/${id}/parts`, { ...data, dryRun: true }),
  archive:    (id: string)                                    => del<void>(`/programs/${id}`),
  /**
   * Action groupée sur une sélection.
   *
   * `dryRun` rend le plan sans rien écrire : l'interface s'en sert pour annoncer
   * ce qui sera touché et ce qui sera écarté avant confirmation. Le serveur
   * rejoue le même calcul à l'écriture, l'aperçu ne peut donc pas mentir.
   */
  bulk: (body: BulkProgramRequest) => post<BulkProgramResult>('/programs/bulk', body),
  restore:    (id: string)                                    => post<Program>(`/programs/${id}/restore`),
  permanentDelete: (id: string)                               => del<void>(`/programs/${id}/permanent`),
  deleteFromCatalog: async (id: string, wasActive = true) => {
    if (wasActive) await del<void>(`/programs/${id}`)
    try {
      await del<void>(`/programs/${id}/permanent`)
    } catch (error) {
      if (wasActive) await post<Program>(`/programs/${id}/restore`).catch(() => undefined)
      throw error
    }
  },
  duplicate:  (id: string)                                    => post<Program>(`/programs/${id}/duplicate`),
  listRelatedEditions: (id: string)                            => get<RelatedEditionsResult>(`/programs/${id}/related-editions`),
  mediaSuggestions: (id: string)                              => get<ProgramMediaSuggestion[]>(`/programs/${id}/media-suggestions`),
  confirmMediaSuggestions: (id: string, links: Array<{ programId: string; filePath: string }>) =>
    post<Program>(`/programs/${id}/media-suggestions/confirm`, { links }),
  listMedia:  (programId: string)                             => get<MediaAsset[]>(`/programs/${programId}/media`),
  addMedia:   (programId: string, data: Partial<MediaAsset>)  => post<MediaAsset>(`/programs/${programId}/media`, data),
  updateMedia:(assetId: string, data: Partial<MediaAsset>)    => patch<MediaAsset>(`/media/${assetId}`, data),
  deleteMedia:(assetId: string)                               => del<void>(`/media/${assetId}`),
  // ── Miniatures ─────────────────────────────────────────────────────────
  listThumbnails: (programId: string) =>
    get<ProgramThumbnails>(`/programs/${programId}/thumbnails`),
  generateThumbnails: (programId: string) =>
    post<ProgramThumbnails>(`/programs/${programId}/thumbnails/generate`),
  selectThumbnail: (programId: string, url: string | null) =>
    patch<ProgramThumbnails>(`/programs/${programId}/thumbnails`, { url }),
  clearThumbnails: (programId: string) =>
    del<void>(`/programs/${programId}/thumbnails`),
  importThumbnail: async (programId: string, file: File): Promise<ProgramThumbnails> => {
    const body = new FormData()
    body.append('file', file)
    // FormData porte sa propre frontière multipart : surtout ne pas fixer
    // Content-Type à la main, le navigateur doit l'écrire.
    return httpRequest<ProgramThumbnails>(`/programs/${programId}/thumbnails/import`, { method: 'POST', body })
  },

  checkMedia: (assetId: string)                               => post<MediaCheckResult>(`/media/${assetId}/check`),

  /**
   * Crée un direct ad-hoc : programme caché du catalogue + son ScheduleItem LIVE.
   * Utilisé depuis AddItemModal en mode "Direct ad-hoc".
   */
  createAdHocLive: (data: {
    channelId:    string
    gridId:       string
    title:        string
    liveSourceId: string
    liveStartMode?: 'AUTO' | 'MANUAL'
    liveEndMode?: 'AUTO' | 'MANUAL'
    liveHoldClipId?: string | null
    startTime:    string   // ISO
    durationSec:  number
    partDurationsSec?: number[]
    interPartBreakSec?: number
    createReplayProgram?: boolean
    titleShort?:  string
    epgSubtitle?: string
    summary?:     string
    genre?:       string
    tags?:        string[]
    director?:    string
    presenter?:   string
    production?:  string
    year?:        number
    isNew?:       boolean
    // Nomenclature Plurimedia. Sans fiche programme (rediffusion décochée), ce
    // sont les seules valeurs que l'export EPG ait à publier pour ce direct.
    epgFederation?: string
    epgThemeId?:  number
    epgShowId?:   number
    bugType?:     'NONE' | 'LMTV_SARTHE' | 'LMTV_SARTHE_DIRECT' | 'LMTV_SARTHE_INEDIT'
    logoProfileId?: string | null
  }) => post<{ program: Program | null; item: any; items: any[]; bundleId: string | null; conflicts: any[]; startShiftedSec?: number }>('/programs/ad-hoc-live', data),
}

// ─── Grids ────────────────────────────────────────────────────────────────────

export const gridsApi = {
  list:          (q: any = {})                          => get<any>(`/grids${toQS(q)}`),
  getById:       (id: string)                                 => get<ScheduleGrid>(`/grids/${id}`),
  create:        (data: any)                    => post<ScheduleGrid>('/grids', data),
  update:        (id: string, data: Partial<any>) => patch<ScheduleGrid>(`/grids/${id}`, data),
  archive:       (id: string)                                 => del<void>(`/grids/${id}`),
  permanentDelete: (id: string)                               => del<void>(`/grids/${id}/permanent`),
  validate:      (id: string, autoFix = false)                => post<ValidationResult>(`/grids/${id}/validate`, { autoFixOverlaps: autoFix }),
  /** Recale un chevauchement (paire) ou tous ceux d'une journée d'antenne. */
  shiftOverlaps: (id: string, target: { overItemId: string; itemId: string } | { date: string }) =>
    post<ShiftOverlapsResult>(`/grids/${id}/shift-overlaps`, target),
  exportPlaylist:(id: string)                                 => post<PlaylistExport>(`/grids/${id}/export`),
  listItems:     (gridId: string, from?: string, to?: string) => get<ScheduleItem[]>(`/grids/${gridId}/items${toQS({ from, to })}`),
  getItems:      (gridId: string, from?: string, to?: string) => get<ScheduleItem[]>(`/grids/${gridId}/items${toQS({ from, to })}`),
  export:        (id: string) => post<PlaylistExport>(`/grids/${id}/export`),
}

/** Écriture d'un créneau : ses colonnes, plus la suite ordonnée d'un créneau
 *  libre — envoyée comme une liste d'identifiants, l'ordre portant le sens. */
export type TemplateSlotWrite = Partial<TemplateSlot> & { entryProgramIds?: string[] }

export interface TemplateRollOptions {
  jingles: Array<Pick<Program, 'id' | 'title' | 'durationSec' | 'type'>>
  adSpots: Array<{
    id: string
    title: string
    durationSec: number
    active: boolean
    campaign: { id: string; name: string } | null
  }>
}

export const gridTemplatesApi = {
  list:      (channelId: string, archived: 'active' | 'archived' | 'all' = 'active') => get<ScheduleGrid[]>(`/grid-templates${toQS({ channelId, archived })}`),
  rollOptions: (channelId: string) => get<TemplateRollOptions>(`/grid-templates/roll-options${toQS({ channelId })}`),
  getById:   (id: string) => get<ScheduleGrid & { templateSlots: TemplateSlot[] }>(`/grid-templates/${id}`),
  create:    (data: { channelId: string; name: string }) => post<ScheduleGrid>('/grid-templates', data),
  update:    (id: string, data: { name?: string }) => patch<ScheduleGrid>(`/grid-templates/${id}`, data),
  archive:   (id: string) => del<void>(`/grid-templates/${id}`),
  restore:   (id: string) => post<ScheduleGrid>(`/grid-templates/${id}/restore`),
  permanentDelete: (id: string, deleteHistory = false) => del<{
    deletedGenerations: number
    detachedGeneratedItems: number
    detachedReplacedItems: number
  }>(`/grid-templates/${id}/permanent${toQS({ deleteHistory: deleteHistory ? 'true' : undefined })}`),
  duplicate: (id: string) => post<ScheduleGrid>(`/grid-templates/${id}/duplicate`),
  validate:  (id: string) => post<{ valid: boolean; issues: string[] }>(`/grid-templates/${id}/validate`),
  addSlot:   (templateId: string, data: Omit<TemplateSlot, 'id' | 'templateId' | 'createdAt' | 'updatedAt' | 'fixedProgram' | 'series' | 'collection'>) =>
    post<TemplateSlot>(`/grid-templates/${templateId}/slots`, data),
  addSlots: (templateId: string, slots: Array<Omit<TemplateSlot, 'id' | 'templateId' | 'createdAt' | 'updatedAt' | 'fixedProgram' | 'series' | 'collection'>>) =>
    post<TemplateSlot[]>(`/grid-templates/${templateId}/slots/batch`, { slots }),
  updateSlot: (id: string, data: TemplateSlotWrite) => patch<TemplateSlot>(`/grid-template-slots/${id}`, data),
  deleteSlot: (id: string) => del<void>(`/grid-template-slots/${id}`),
  // Recalage du modèle sur les durées réelles du catalogue. `apply: false`
  // n'écrit rien : c'est le plan, relu avant d'être appliqué.
  retime: (id: string, data: { apply?: boolean; anchorSlotIds?: string[] } = {}) =>
    post<TemplateRetimeResult>(`/grid-templates/${id}/retime`, data),
  preview: (id: string, data: {
    targetDate: string
    scope: GenerationScope
    /** Conservé pour compatibilité : le dépôt vise toujours la grille de diffusion. */
    destination?: GenerationDestination
    targetGridId?: string
  }) => post<{ generationId: string; templateVersion: number; proposals: TemplateProposal[] }>(`/grid-templates/${id}/preview`, data),
}

export const gridGenerationsApi = {
  apply: (id: string, decisions: Array<{
    slotId: string
    proposalId?: string
    action?: 'APPLY' | 'SKIP'
    conflictAction?: 'KEEP' | 'REPLACE'
    programId?: string
  }>) => post<{ generationId: string; targetGridId: string; created: number; replaced: number }>(`/grid-generations/${id}/apply`, { decisions }),
  rollback: (id: string) => post<{ status: string; deleted: number; restored: number; preserved: number }>(`/grid-generations/${id}/rollback`),
  recent: (channelId: string) => get<GridGeneration[]>(`/grid-generations${toQS({ channelId })}`),
}

// ─── Items ────────────────────────────────────────────────────────────────────

export const itemsApi = {
  create:       (data: any)                                         => post<ItemWithConflicts>('/items', data),
  duplicate:    (id: string, startTime?: string)                    => post<ScheduleItem & { items?: ScheduleItem[] }>(`/items/${id}/duplicate`, startTime ? { startTime } : {}),
  replaceProgram: (id: string, programId: string)                   => post<ScheduleItem & { items?: ScheduleItem[] }>(`/items/${id}/replace-program`, { programId }),
  /** Fichier posé sur cette diffusion seulement : la fiche et les autres diffusions ne changent pas. */
  assignMediaOnly: (id: string, data: Partial<MediaAsset>)            => post<ScheduleItem>(`/items/${id}/media-only`, data),
  replaceProgramRange: (id: string, data: { programId: string; scope: ReplaceProgramScope; untilDate?: string }) =>
    post<ReplaceProgramRangeResult>(`/items/${id}/replace-program-range`, data),
  replaceJingle: (id: string, programId: string)                   => post<ScheduleItem>(`/items/${id}/replace-jingle`, { programId }),
  rebroadcast:  (data: RebroadcastRequest)                          => post<RebroadcastReport>('/items/rebroadcast', data),
  update:       (id: string, data: Partial<any>)                    => patch<ScheduleItem>(`/items/${id}`, data),
  move:         (id: string, startTime: string)                                     => patch<ItemWithConflicts>(`/items/${id}/move`, { startTime }),
  /** Déplace une sélection d'un même écart, tout ou rien. Les éléments engagés à l'antenne restent en place. */
  moveGroup:    (itemIds: string[], deltaMs: number) =>
    post<{ items: ScheduleItem[]; conflicts: ScheduleItem[]; skippedIds: string[]; gridIds: string[] }>('/items/move-group', { itemIds, deltaMs }),
  /**
   * Fin d'un direct tirée sur la grille : il prend sur l'infoservice/interlude
   * qui le suit, le lui rend en raccourcissant (`lib/live-end-resize-plan.ts`).
   */
  resizeLive: (id: string, endTime: string) =>
    patch<{ itemId: string; gridId: string; endTime: string; deletedItemIds: string[] }>(`/items/${id}/resize-live`, { endTime }),
  resizeInfoservice: (id: string, startTime: string, endTime: string, memberIds: string[]) =>
    patch<ItemWithConflicts & { removedIds: string[] }>(`/items/${id}/resize-infoservice`, { startTime, endTime, memberIds }),
  reorder:      (items: Array<{ id: string; sortOrder: number; startTime?: string }>) => post<{ updated: number }>('/items/reorder', { items }),
  delete:       (id: string)                                                     => del<void>(`/items/${id}`),
  updateStatus: (id: string, status: string)                                     => patch<ScheduleItem>(`/items/${id}/status`, { status }),
  /**
   * Déroulé estimé d'un bloc de direct : la liste remplace la précédente, et un
   * tableau vide efface l'estimation. Route séparée de `update` à dessein — le
   * déroulé est une annotation, modifiable même pendant l'émission, alors que
   * la modification d'un item à l'antenne est refusée.
   */
  setLiveParts: (id: string, parts: Array<{ title?: string | null; durationSec: number; breakAfterSec?: number }>) =>
    put<ScheduleItem>(`/items/${id}/live-parts`, { parts }),
  /**
   * Ajustement antenne du déroulé : durée tenue par partie, `null` = retour au
   * calcul automatique. Ne touche ni la durée annoncée ni l'EPG.
   */
  setLivePartsOnAir: (id: string, parts: Array<{ partIndex: number; durationSec: number | null }>) =>
    put<ScheduleItem>(`/items/${id}/live-parts/on-air`, { parts }),
}

// ─── Playout ──────────────────────────────────────────────────────────────────

export const playoutApi = {
  getState:       (channelId: string)              => get<any>(`/playout/${channelId}`),
  getTimingTrace: (channelId: string, limit = 200, filters: { event?: string; itemId?: string } = {}) =>
    get<{ channelId: string; entries: import('../types/index.js').TimingTraceEntry[] }>(`/playout/${channelId}/timing-trace${toQS({ limit, event: filters.event, itemId: filters.itemId })}`),
  start:          (channelId: string)              => post<OkResponse>(`/playout/${channelId}/start`),
  stop:           (channelId: string)              => post<OkResponse>(`/playout/${channelId}/stop`),
  skip:                (channelId: string)              => post<OkResponse>(`/playout/${channelId}/skip`),
  skipAndReschedule:   (channelId: string)              => post<OkResponse>(`/playout/${channelId}/skip-reschedule`),
  forceInterlude: (channelId: string, clipId?: string, active?: boolean) => post<OkResponse>(`/playout/${channelId}/interlude`, { clipId, active }),
  emergencyStop:  (channelId: string)              => post<OkResponse>(`/playout/${channelId}/emergency`),

  // CG (Habillage)
  showBug:            (channelId: string, bugType?: string) => post<OkResponse>(`/playout/${channelId}/cg/bug`, { bugType }),
  hideBug:            (channelId: string) => del<void>(`/playout/${channelId}/cg/bug`),

  // Direct live
  goLive: (channelId: string, itemId: string) =>
    post<OkResponse & { itemId?: string; lateSec?: number }>(`/playout/${channelId}/go-live`, { itemId }),
  /**
   * Coupe un direct « Fin manuelle » à la demande de l'opérateur. Le backend
   * décide seul du remplissage du trou : jingle de transition puis infoservice
   * jusqu'au raccord si la fenêtre dépasse 10 s, interlude sinon, avec un
   * jingle de sortie quand l'antenne recollerait sec sur un programme.
   */
  endLive: (channelId: string, itemId: string) =>
    post<EndLiveResult>(`/playout/${channelId}/end-live`, { itemId }),

  /** Plan de coupure proposé, sans rien écrire : sert à pré-remplir la boîte de choix. */
  endLivePlan: (channelId: string, itemId: string) =>
    get<EndLivePlan>(`/playout/${channelId}/end-live-plan?itemId=${encodeURIComponent(itemId)}`),

  // ── Décrochage d'un bloc de direct ────────────────────────────────────────
  /** Aperçu du décrochage, sans rien écrire. */
  liveBreakPlan: (channelId: string, body: LiveBreakRequest) =>
    post<LiveBreakPlanResult>(`/playout/${channelId}/live-break-plan`, body),
  /** Creuse le décrochage dans le bloc : troncature, comblage, reprise. */
  liveBreak: (channelId: string, body: LiveBreakRequest) =>
    post<LiveBreakResult>(`/playout/${channelId}/live-break`, body),
  /** Raccroche : écourte le comblage et avance la reprise du direct. */
  liveResume: (channelId: string, itemId?: string) =>
    post<LiveResumeResult>(`/playout/${channelId}/live-resume`, itemId ? { itemId } : {}),

  /**
   * Pendant un décrochage : prend le direct suivant (autre bloc). Avant son heure
   * de grille, `early` doit être posé — la régie le demande après confirmation.
   */
  liveTake: (channelId: string, itemId: string, early = false) =>
    post<LiveTakeResult>(`/playout/${channelId}/live-take`, early ? { itemId, early } : { itemId }),

  /**
   * Bascule le direct à l'antenne (et ses reprises à venir) sur un autre flux
   * Internet. L'interlude couvre l'ouverture du flux ; retour dès son image.
   */
  switchLiveSource: (channelId: string, itemId: string, liveSourceId: string) =>
    post<OkResponse & { result?: { sourceName?: string; interlude?: boolean; pending?: boolean } | null; message?: string; queued?: boolean }>(
      `/playout/${channelId}/live-source-switch`, { itemId, liveSourceId },
    ),

  /** Direct en ERREUR ou resté en attente après son heure : sa source à l'antenne, maintenant. */
  startLiveNow: (channelId: string, itemId: string) =>
    post<OkResponse & { itemId?: string; lateSec?: number }>(`/playout/${channelId}/start-live-now`, { itemId }),

  /** Décrochage préfait de la journée d'antenne (`AAAA-MM-JJ`). */
  getLiveBreakTemplate: (channelId: string, day: string) =>
    get<LiveBreakTemplate>(`/playout/${channelId}/live-break-template/${day}`),
  saveLiveBreakTemplate: (channelId: string, day: string, body: {
    mode: 'FIXED' | 'OPEN'; windowSec: number | null; items: LiveFillEntry[]
  }) => put<LiveBreakTemplate & OkResponse>(`/playout/${channelId}/live-break-template/${day}`, body),
  deleteLiveBreakTemplate: (channelId: string, day: string) =>
    del<OkResponse>(`/playout/${channelId}/live-break-template/${day}`),

  /**
   * Change le mode de fin d'un direct à l'antenne, dans les deux sens. En manuel
   * l'opérateur décide de la coupure et le minuteur devient un filet de sécurité
   * posé sur la prochaine ancre ; en automatique il revient sur l'heure de grille.
   */
  setLiveEndMode: (channelId: string, itemId: string, mode: 'AUTO' | 'MANUAL') =>
    post<{ ok: boolean; itemId: string; liveEndMode: 'AUTO' | 'MANUAL'; changed: boolean }>(`/playout/${channelId}/live-end-mode`, { itemId, mode }),
  extendLive: (channelId: string, itemId: string, additionalSec: number) =>
    post<{ ok: boolean; newEndTime: string; newDurationSec: number; gapSec: number; gapStart: string; gapEnd: string | null; nextItemId: string | null; nextItemTitle: string | null }>(`/playout/${channelId}/extend-live`, { itemId, additionalSec }),
  getGapFillOptions: (channelId: string, date?: string) =>
    get<GapFillOptions>(`/playout/${channelId}/gap-fill-options${toQS({ date })}`),
  fillGap: (channelId: string, data: { gridId: string; startTime: string; endTime: string; items: GapFillEntry[] }) =>
    post<{ created: number; items: any[]; filledSec: number; remainingSec: number }>(`/playout/${channelId}/fill-gap`, data),

  /** Catalogue du mode « combler tous les trous » : habillages, campagnes actives, infoservice du jour. */
  getAutoGapFillCatalog: (channelId: string, date: string) =>
    get<AutoGapFillCatalog>(`/playout/${channelId}/auto-gap-fill/catalog${toQS({ date })}`),
  /** Plan complet sur tous les trous, sans rien écrire. */
  previewAutoGapFill: (channelId: string, data: AutoGapFillRequest) =>
    post<AutoGapFillPlan>(`/playout/${channelId}/auto-gap-fill/preview`, data),
  /** Pose le plan ; chaque trou est écrit dans sa propre transaction. */
  applyAutoGapFill: (channelId: string, data: AutoGapFillRequest) =>
    post<AutoGapFillResult>(`/playout/${channelId}/auto-gap-fill`, data),
  /** Passes de comblage de la journée, la plus récente en tête. */
  listAutoGapFillRuns: (channelId: string, date: string) =>
    get<AutoGapFillRun[]>(`/playout/${channelId}/auto-gap-fill/runs${toQS({ date })}`),
  /** Retire tout ce qu'une passe a posé, hors éléments déjà passés à l'antenne. */
  revertAutoGapFillRun: (channelId: string, runId: string) =>
    del<{ removedCount: number; keptCount: number; gridIds: string[] }>(`/playout/${channelId}/auto-gap-fill/runs/${runId}`),

  /**
   * Termine l'item à l'antenne plus tôt que prévu et comble le trou laissé devant le
   * programme suivant. La grille n'est pas recalée : les programmes suivants
   * gardent leur heure d'antenne.
   */
  liveSkipFill: (channelId: string, data: {
    itemId: string
    leadSec?: number
    /** Heure précise de coupure, utilisée par le recalage automatique. */
    cutAt?: string
    compact?: boolean
    /** Recalage automatique : refuse la coupure si le remplissage ne comble pas le trou à la seconde. */
    requireExactFill?: boolean
    items: LiveFillEntry[]
  }) =>
    post<LiveSkipFillResult>(`/playout/${channelId}/live-skip-fill`, data),
}

export type PlayoutTargetInput = Pick<PlayoutTarget, 'name' | 'host' | 'port' | 'casparChannel' | 'fps' | 'routerInput' | 'priority' | 'enabled'>
export type VideoRouterInput = Pick<VideoRouter, 'host' | 'port' | 'outputIndex' | 'enabled'>

export const redundancyApi = {
  get: (channelId: string) => get<RedundancyConfigResponse>(`/redundancy/${channelId}`),
  createTarget: (channelId: string, data: PlayoutTargetInput) => post<PlayoutTarget & { commandId?: string }>(`/redundancy/${channelId}/targets`, data),
  updateTarget: (channelId: string, targetId: string, data: Partial<PlayoutTargetInput>) => patch<PlayoutTarget & { commandId?: string }>(`/redundancy/${channelId}/targets/${targetId}`, data),
  deleteTarget: (channelId: string, targetId: string) => del<void>(`/redundancy/${channelId}/targets/${targetId}`),
  saveRouter: (channelId: string, data: VideoRouterInput) => put<VideoRouter>(`/redundancy/${channelId}/router`, data),
  setArmed: (channelId: string, armed: boolean) => patch<{ ok: boolean; queued?: boolean; commandId?: string }>(`/redundancy/${channelId}/armed`, { armed }),
  switchTarget: (channelId: string, targetId: string) => post<{ ok: boolean; queued?: boolean; commandId?: string }>(`/redundancy/${channelId}/switch`, { targetId }),
  testTarget: (channelId: string, targetId: string) => post<Record<string, unknown>>(`/redundancy/${channelId}/targets/${targetId}/test`),
  testRouter: (channelId: string) => post<Record<string, unknown>>(`/redundancy/${channelId}/router/test`),
}

// ─── Remplissage de trou (options + entrées) ──────────────────────────────────

export interface GapFillSpot     { id: string; title: string; durationSec: number; airingsCount: number }
export interface GapFillCampaign { id: string; name: string; advertiserName: string | null; spots: GapFillSpot[]; totalDurationSec: number; spotCount: number }
export interface GapFillJingle   { id: string; title: string; type: string; durationSec: number; mediaAssetId: string | null }
export interface GapFillInfoservice { spotId: string; title: string; durationSec: number; mediaDate: string | null }

export interface GapFillOptions {
  clips:        InterludeClip[]
  campaigns:    GapFillCampaign[]
  jingles?:     GapFillJingle[]
  infoservice?: GapFillInfoservice | null
  transitionDurationMs: number
  /** Jingle de retour au direct réglé sur la chaîne, s'il est diffusable. */
  resumeJingle?: { title: string; durationSec: number } | null
}

export interface GapFillEntry {
  itemType:      'AD_BREAK' | 'INFOSERVICE' | 'JINGLE' | 'INTERLUDE'
  durationSec?:  number
  fillRest?:     boolean
  spotIds?:      string[]
  clipId?:       string
  programId?:    string
  mediaAssetId?: string
}

// ─── Comblage automatique de toute la journée ─────────────────────────────────

export interface AutoGapFillJingle {
  programId:    string
  mediaAssetId: string
  title:        string
  type:         string
  durationSec:  number
}

export interface AutoGapFillSpot {
  id:           string
  title:        string
  durationSec:  number
  campaignId:   string | null
  campaignName: string | null
}

export interface AutoGapFillCampaign {
  id:                string
  name:              string
  advertiserName:    string | null
  targetSlots:       string[]
  maxDailyFrequency: number | null
  /** Passages déjà posés aujourd'hui pour cette campagne. */
  placedToday:       number
  spots:             AutoGapFillSpot[]
}

export interface AutoGapFillCatalog {
  jingles:       AutoGapFillJingle[]
  campaigns:     AutoGapFillCampaign[]
  infoservice:   { spotId: string; title: string; durationSec: number; mediaDate: string | null } | null
  transitionSec: number
}

export interface AutoGapFillOptions {
  openerProgramIds:  string[]
  closerProgramIds:  string[]
  /** `null` = tous les spots des campagnes actives du jour. */
  spotIds:           string[] | null
  useInfoservice:    boolean
  minInfoserviceSec: number
  infoserviceRepeat: boolean
  maxSpotsPerGap:    number | null
  respectDailyCap:   boolean
}

export interface AutoGapFillRequest {
  date:    string
  gaps:    Array<{ gridId: string; startTime: string; endTime: string }>
  options: AutoGapFillOptions
}

export type AutoGapFillStep =
  | { kind: 'JINGLE'; role: 'OPENER' | 'CLOSER'; programId: string; mediaAssetId: string; title: string; durationSec: number }
  | { kind: 'AD_BREAK'; spots: AutoGapFillSpot[]; durationSec: number }
  | { kind: 'INFOSERVICE'; title: string; durationSec: number; passages: number }
  /** Temps laissé au bouche-trou du playout, posé avant l'habillage de fermeture. */
  | { kind: 'FREE'; durationSec: number }

export interface AutoGapFillPlannedGap {
  gridId:       string
  startTime:    string
  endTime:      string
  gapSec:       number
  steps:        AutoGapFillStep[]
  filledSec:    number
  remainingSec: number
  skipped:      boolean
  reason:       'NOTHING_FITS' | 'NO_CONTENT' | null
}

export interface AutoGapFillPlan {
  gaps:         AutoGapFillPlannedGap[]
  plannedCount: number
  skippedCount: number
  spotCount:    number
  totalGapSec:  number
  filledSec:    number
  remainingSec: number
  infoserviceMissing?: boolean
}

export interface AutoGapFillOutcome {
  startTime:  string
  endTime:    string
  gapSec:     number
  status:     'FILLED' | 'SKIPPED' | 'FAILED'
  createdIds: string[]
  filledSec:  number
  message:    string | null
}

export interface AutoGapFillRun {
  id:             string
  broadcastDay:   string
  createdAt:      string
  itemCount:      number
  gapCount:       number
  /** Éléments encore en grille, donc encore retirables. */
  remainingCount: number
  /** Éléments déjà passés ou en cours, qu'un retrait laissera en place. */
  frozenCount:    number
  revertedAt:     string | null
}

export interface AutoGapFillResult {
  /** `null` quand la passe n'a rien posé. */
  runId:        string | null
  plan:         AutoGapFillPlan
  outcomes:     AutoGapFillOutcome[]
  filledCount:  number
  failedCount:  number
  createdCount: number
  gridIds:      string[]
}

/** Façon de tirer les spots d'une campagne à chaque pose du décrochage. */
export type AdCampaignPickStrategy = 'LEAST_AIRED' | 'RANDOM'

export interface LiveFillEntry {
  itemType:      'AD_BREAK' | 'AD_CAMPAIGN' | 'INFOSERVICE' | 'JINGLE' | 'INTERLUDE' | 'PROGRAM'
  /**
   * Durée de l'élément. Sur une campagne (`AD_CAMPAIGN`), c'est la place
   * réservée : le tirage du serveur n'en sortira jamais, transitions comprises.
   */
  durationSec?:  number
  /** Infoservice uniquement : occupe tout l'espace restant, dimensionné par le serveur. */
  fillRest?:     boolean
  spotId?:       string
  clipId?:       string
  programId?:    string
  mediaAssetId?: string
  /** Campagne dont les spots sont tirés au moment de poser le décrochage. */
  campaignId?:   string
  pick?:         AdCampaignPickStrategy
  spotCount?:    number
}

/**
 * Décrochage d'un bloc de direct. `mode: 'OPEN'` = sans durée fixe : le comblage
 * tient jusqu'à la fin du bloc et c'est le raccrochage qui créera la reprise.
 * `resume: false` = le direct ne reprend pas, le bloc s'arrête sur ce décrochage.
 */
export interface LiveBreakRequest {
  itemId:     string
  mode:       'FIXED' | 'OPEN'
  windowSec?: number
  resume:     boolean
  items:      LiveFillEntry[]
  leadSec?:   number
}

export interface LiveBreakPlanStep {
  itemType:    'AD_BREAK' | 'INFOSERVICE' | 'JINGLE' | 'INTERLUDE' | 'PROGRAM'
  startTime:   string
  endTime:     string
  durationSec: number
}

export interface LiveBreakPlanResult {
  breakAt:     string
  windowSec:   number
  resumeStart: string | null
  blockEnd:    string
  windowEnd:   string
  leadSec:     number
  steps:       LiveBreakPlanStep[]
  resumeJingle?: { title: string; startTime: string; endTime: string; durationSec: number } | null
  /** Jingle de retour réglé, mais sans la place : le direct reprend sans. */
  resumeJingleSkipped?: boolean
  /** Jingle de décrochage (même jingle que le retour), en tête du comblage. */
  openingJingle?: { title: string; startTime: string; endTime: string; durationSec: number } | null
  /** Jingle de décrochage dû, mais sans la place : le comblage part sans. */
  openingJingleSkipped?: boolean
}

export interface LiveBreakResult extends LiveBreakPlanResult {
  ok:           boolean
  resumeItemId: string | null
  created:      number
  items:        Array<{ id: string; itemType: string; startTime: string; endTime: string }>
}

export interface LiveResumeResult {
  ok:                 boolean
  resumeAt:           string
  /** Heure de reprise réelle du direct : après le jingle de retour s'il y en a un. */
  liveStart:          string
  resumeItemId:       string
  jingleItemId:       string | null
  removedFillItems:   number
  /** Vrai quand un bloc pub était en cours : la reprise est calée sur sa fin. */
  deferredForAdBreak: boolean
  message:            string
}

export interface LiveTakeResult {
  ok:                 boolean
  resumeAt:           string
  liveStart:          string
  liveEnd:            string
  takenItemId:        string
  jingleItemId:       string | null
  removedFillItems:   number
  deferredForAdBreak: boolean
  message:            string
}

export interface LiveBreakTemplate {
  broadcastDay: string
  mode:         'FIXED' | 'OPEN'
  windowSec:    number | null
  items:        LiveFillEntry[]
  updatedAt?:   string
}

export interface EndLiveResult {
  ok:          boolean
  cutAt:       string
  fill:        'INFOSERVICE' | 'INTERLUDE' | 'NONE'
  windowSec:   number
  infoservice: number
  jingle:      boolean
  jingles:     number
}

/** Un élément du plan de coupure proposé par le serveur. */
export interface EndLivePlanStep {
  role:         'LEAD_JINGLE' | 'INFOSERVICE' | 'TAIL_JINGLE'
  itemType:     'JINGLE' | 'INFOSERVICE'
  startTime:    string
  endTime:      string
  durationSec:  number
  title:        string
  programId:    string | null
  mediaAssetId: string | null
}

/**
 * Aperçu de « Fin du direct » : ce que le serveur poserait si l'opérateur
 * validait sans rien changer. Sert à pré-remplir la boîte de choix.
 */
export interface EndLivePlan {
  cutAt:     string
  fill:      'INFOSERVICE' | 'INTERLUDE' | 'NONE'
  windowSec: number
  leadSec:   number
  boundary:  { startTime: string; itemType: string; title: string | null } | null
  /**
   * Direct de même source qui attend derrière : il partira à la fin de la
   * composition et gardera sa fin de grille. Absent des serveurs antérieurs.
   */
  handover?: { itemId: string; title: string | null; originalStartTime: string; endTime: string } | null
  /** Pubs posées pendant le débordement, jamais diffusées : proposées, cochées. */
  eatenAds?: Array<{ spotId: string; title: string; durationSec: number }>
  steps:     EndLivePlanStep[]
}

export interface LiveSkipFillResult {
  ok:           boolean
  liveEndTime:  string
  leadSec:      number
  /** Présent quand un direct de même source a pris le relais. */
  handover?: {
    nextItemId:    string | null
    nextTitle:     string | null
    nextStartTime: string | null
    nextEndTime:   string | null
    skipped:       Array<{ id: string; title: string }>
  }
  /** Tampons (pubs, jingles, infoservice) remontés contre la fin de l'item. */
  movedItems:   number
  shiftedBySec: number
  gapStart:     string
  gapEnd:       string
  gapSec:       number
  filledSec:    number
  remainingSec: number
  created:      number
  items:        Array<{ id: string; itemType: string; startTime: string; endTime: string }>
}

// ─── Live sources / Interludes ────────────────────────────────────────────────

export const liveSourcesApi = {
  list: (channelId: string) => get<LiveSource[]>(`/live-sources${toQS({ channelId })}`),
}

export const interludeApi = {
  list: (channelId: string) => get<InterludeClip[]>(`/interlude-clips${toQS({ channelId })}`),
}

export const cgTemplatesApi = {
  history: (id: string) => get<Array<{ revision: string; savedAt: string; name: string; design: import('../types/domain.js').CgDesignerDesign }>>(`/cg-templates/${id}/history`),
  get: (id: string) => get<CgTemplate>(`/cg-templates/${id}`),
  list:   (channelId: string) => get<CgTemplate[]>(`/cg-templates${toQS({ channelId })}`),
  update: (id: string, data: Partial<CgTemplate>) => patch<CgTemplate>(`/cg-templates/${id}`, data),
  create: (data: Partial<CgTemplate> & { channelId: string }) => post<CgTemplate>('/cg-templates', data),
  delete: (id: string) => del<void>(`/cg-templates/${id}`),
  saveDesigner: (data: CgDesignerTemplatePayload) => post<CgDesignerTemplateResult>('/cg-templates/designer', data),
  // Roue crantée : renommer l'identifiant déplace aussi le fichier HTML.
  updateSettings: (id: string, data: { name?: string; slug?: string | null; manualOnGrid?: boolean; active?: boolean; expectedRevision?: string }) =>
    patch<CgTemplate>(`/cg-templates/${id}/settings`, data),
  // Envoi à la main depuis la grille : c'est le modèle qu'on affiche, pas une règle.
  show:   (channelId: string, templateId: string) => post<OkResponse>(`/playout/${channelId}/cg/template/${templateId}/show`),
  hide:   (channelId: string, templateId: string) => del<void>(`/playout/${channelId}/cg/template/${templateId}`),
  // Bouton "Tester" : sort sur le canal CasparCG de test, jamais sur le canal de diffusion.
  test:     (channelId: string, templateId: string) => post<OkResponse>(`/playout/${channelId}/cg/template/${templateId}/test`),
  hideTest: (channelId: string, templateId: string) => del<void>(`/playout/${channelId}/cg/template/${templateId}/test`),
  // Superposition : liste du dessus vers le dessous, appliquée tout de suite à l'antenne.
  layerOrder: (channelId: string) =>
    get<{ templates: import('../types/domain.js').CgLayerOrderEntry[] }>(`/cg-templates/layer-order${toQS({ channelId })}`),
  setLayerOrder: (channelId: string, templateIds: string[]) =>
    put<import('../types/domain.js').CgLayerOrderResult>('/cg-templates/layer-order', { channelId, templateIds }),
}

export const logoProfilesApi = {
  list:   (channelId: string, includeInactive = false) =>
    get<LogoProfile[]>(`/logo-profiles${toQS({ channelId, includeInactive: includeInactive ? 'true' : undefined })}`),
  create: (data: { channelId: string; name: string; key?: string | null; active?: boolean }) =>
    post<LogoProfile>('/logo-profiles', data),
  update: (id: string, data: Partial<Pick<LogoProfile, 'name' | 'key' | 'active' | 'manualOnGrid' | 'sortOrder'>> & { expectedRevision?: string }) =>
    patch<LogoProfile>(`/logo-profiles/${id}`, data),
  delete: (id: string) => del<void>(`/logo-profiles/${id}`),
}

function withCgAssetUrl(asset: CgAsset): CgAsset {
  return { ...asset, url: `${BASE_URL}/cg-assets/${encodeURIComponent(asset.id)}/file` }
}

export const cgAssetsApi = {
  list: async (channelId: string): Promise<CgAsset[]> => {
    const assets = await get<CgAsset[]>(`/cg-assets${toQS({ channelId })}`)
    return assets.map(withCgAssetUrl)
  },
  upload: async (channelId: string, file: File): Promise<CgAsset> => {
    const form = new FormData()
    form.append('file', file)
    return withCgAssetUrl(await httpRequest<CgAsset>(`/cg-assets${toQS({ channelId })}`, { method: 'POST', body: form }))
  },
  rename: async (id: string, name: string): Promise<CgAsset> =>
    withCgAssetUrl(await patch<CgAsset>(`/cg-assets/${id}`, { name })),
  delete: (id: string) => del<void>(`/cg-assets/${id}`),
}

function withCgFontUrl(font: CgFont): CgFont {
  return { ...font, url: `${BASE_URL}/cg-fonts/${encodeURIComponent(font.id)}/file` }
}

export const cgFontsApi = {
  list: async (): Promise<CgFont[]> => {
    const fonts = await get<CgFont[]>('/cg-fonts')
    return fonts.map(withCgFontUrl)
  },
  upload: async (file: File): Promise<CgFont> => {
    const form = new FormData()
    form.append('file', file)
    return withCgFontUrl(await httpRequest<CgFont>('/cg-fonts', { method: 'POST', body: form }))
  },
  rename: async (id: string, name: string): Promise<CgFont> =>
    withCgFontUrl(await patch<CgFont>(`/cg-fonts/${id}`, { name })),
  delete: (id: string) => del<void>(`/cg-fonts/${id}`),
}

export const cgOverlaysApi = {
  list:    (channelId: string) => get<CgOverlay[]>(`/cg-overlays${toQS({ channelId })}`),
  getById: (id: string) => get<CgOverlay>(`/cg-overlays/${id}`),
  create:  (data: Partial<CgOverlay> & { channelId: string }) => post<CgOverlay>('/cg-overlays', data),
  update:  (id: string, data: Partial<CgOverlay>) => patch<CgOverlay>(`/cg-overlays/${id}`, data),
  delete:  (id: string) => del<void>(`/cg-overlays/${id}`),
  /** Prochains passages d'une regle en cours d'edition. Lecture seule. */
  previewPassages: (data: {
    channelId: string
    rule: Pick<CgOverlay, 'anchor' | 'offsetSec' | 'durationSec' | 'repeatIntervalSec' | 'hideBeforeEndSec' | 'programScope' | 'targetSeriesIds' | 'targetProgramIds' | 'targetCategoryIds' | 'validityPeriods'>
    limit?: number
  }) => post<CgOverlayUpcomingPassage[]>('/cg-overlays/preview-passages', data),
}

// ─── Récurrence ───────────────────────────────────────────────────────────────

export const recurrenceApi = {
  list:           (channelId: string)                     => get<RecurrenceRule[]>(`/recurrences${toQS({ channelId })}`),
  getById:        (id: string)                            => get<RecurrenceRule>(`/recurrences/${id}`),
  create:         (data: CreateRecurrencePayload)         => post<{ rule: RecurrenceRule; count: number }>('/recurrences', data),
  deleteAll:      (id: string)                            => del<void>(`/recurrences/${id}`),
  deleteFromHere: (id: string, itemId: string)            => del<void>(`/recurrences/${id}/from/${itemId}`),
  deleteOne:      (itemId: string)                        => del<void>(`/items/${itemId}/recurrence`),
}

// ─── Upload ───────────────────────────────────────────────────────────────────

export interface UploadResult {
  fileName:     string
  filePath:     string
  codec:        string | null
  container:    string | null
  durationSec:  number | null
  resolution:   string | null
  frameRate:    number | null
  fileSizeBytes: string | null
  status:       string
}

export const uploadApi = {
  uploadVideo: async (file: File): Promise<UploadResult> => {
    const form = new FormData()
    form.append('file', file)
    return httpRequest<UploadResult>('/upload', { method: 'POST', body: form })
  },
}

// ─── Médiathèque ────────────────────────────────────────────────────────────

export interface MediaLibraryAsset {
  id:            string
  fileName:      string
  filePath:      string
  status:        string
  isProxy:       boolean
  codec:         string | null
  container:     string | null
  durationSec:   number | null
  resolution:    string | null
  fileSizeBytes: string | null
  lastChecked:   string | null
  lastError:     string | null
  updatedAt:     string
  program:       { id: string; title: string } | null
}

export interface MediaOpBlocker {
  itemId: string; channelName: string; title: string
  status: string; startTime: string; endTime: string
}

/** Pictogramme d'état d'un fichier de la médiathèque (voir `media-library-browse.ts`). */
export type MediaEntryState = 'FAILED' | 'SCHEDULED' | 'CHECKED'

/** Entrée de dossier annotée par la base et la grille (`GET /media-library/browse`). */
export interface MediaBrowseEntry {
  name:             string
  path:             string
  relativePath:     string
  casparPath?:      string
  size:             number
  ext?:             string
  type:             'file' | 'directory'
  modifiedAt:       string | null
  itemCount?:       number | null
  durationSec?:     number | null
  assetStatus?:     string | null
  state?:           MediaEntryState | null
  nextBroadcastAt?: string | null
  upcomingCount?:   number
}

export interface MediaBrowseResult {
  path:    string
  subPath: string
  files:   MediaBrowseEntry[]
  disk:    { freeBytes: number; totalBytes: number } | null
}

export interface MediaUpcomingBroadcast {
  startTime: string; title: string; channelName: string; status: string
}

/** Fiche technique d'un fichier pour la fenêtre d'aperçu. */
export interface MediaDetails {
  name:            string
  relativePath:    string
  casparPath:      string
  directory:       string
  size:            number
  modifiedAt:      string | null
  codec:           string | null
  container:       string | null
  durationSec:     number | null
  durationMs:      number | null
  resolution:      string | null
  frameRate:       number | null
  fieldOrder:      string | null
  bitrate:         number | null
  audioCodec:      string | null
  audioLayout:     string | null
  audioSampleRate: number | null
  error?:          string
  broadcasts:      { total: number; upcoming: MediaUpcomingBroadcast[] }
}

/** Résultat d'un envoi de la médiathèque (dépôt ou remplacement). */
export interface MediaLibraryUploadResult extends UploadResult {
  relativePath:         string
  replaced?:            boolean
  assetsUpdated?:       number
  previousDurationSec?: number | null
}

// Morceaux courts : chacun tient sous la coupure ~20 s / ~6 Mo de l'accès distant
// public (funnel). 2 Mio ≈ quelques secondes même à débit bridé.
const UPLOAD_CHUNK_SIZE = 2 * 1024 * 1024
const UPLOAD_CHUNK_RETRIES = 3

/** Envoie un morceau via XHR (progression + cookies de session). */
function putUploadChunk(
  uploadId: string, blob: Blob, offset: number, base: number, total: number,
  onProgress?: (loaded: number, total: number) => void, signal?: AbortSignal,
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', `${BASE_URL}/media-library/upload/chunk?uploadId=${encodeURIComponent(uploadId)}&offset=${offset}`)
    xhr.withCredentials = true
    xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProgress?.(base + e.loaded, total) }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) { onProgress?.(base + blob.size, total); resolve() }
      else {
        let body: unknown
        try { body = xhr.responseText ? JSON.parse(xhr.responseText) : undefined } catch { body = undefined }
        const b = body as { error?: string; message?: string } | undefined
        reject(new HttpError(xhr.status, b?.error ?? 'UPLOAD_FAILED', b?.message ?? `HTTP ${xhr.status}`, b))
      }
    }
    xhr.onerror = () => reject(new HttpError(0, 'NETWORK_ERROR', 'Échec réseau pendant l’envoi'))
    xhr.onabort = () => reject(new DOMException('Envoi annulé', 'AbortError'))
    if (signal) {
      if (signal.aborted) { xhr.abort(); return }
      signal.addEventListener('abort', () => xhr.abort(), { once: true })
    }
    const form = new FormData()
    form.append('chunk', blob, 'chunk')
    xhr.send(form)
  })
}

/** Réessaie un morceau coupé par le funnel : c'est ce qui rend l'envoi fiable. */
async function sendUploadChunk(
  uploadId: string, blob: Blob, offset: number, base: number, total: number,
  onProgress?: (loaded: number, total: number) => void, signal?: AbortSignal,
): Promise<void> {
  let lastErr: unknown
  for (let attempt = 0; attempt < UPLOAD_CHUNK_RETRIES; attempt++) {
    if (signal?.aborted) throw new DOMException('Envoi annulé', 'AbortError')
    try { await putUploadChunk(uploadId, blob, offset, base, total, onProgress, signal); return }
    catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') throw err
      lastErr = err
      await new Promise(r => setTimeout(r, 500 * (attempt + 1)))
    }
  }
  throw lastErr
}

interface ChunkedHandlers {
  onProgress?: (loaded: number, total: number) => void
  signal?:     AbortSignal
}

/**
 * Envoi chunké, avec progression et annulation. Le fichier part en morceaux
 * courts (init → chunks → finish) : chaque requête reste sous la coupure
 * ~20 s / ~6 Mo qu'impose l'accès distant public (funnel Tailscale), qui
 * tuait un envoi monolithique. `fetch` n'exposant pas la progression d'upload,
 * les morceaux passent par XHR.
 */
async function sendChunked(
  file: File,
  initPath: string,
  finishPath: string,
  target: Record<string, string | undefined>,
  { onProgress, signal }: ChunkedHandlers,
): Promise<MediaLibraryUploadResult> {
  if (signal?.aborted) throw new DOMException('Envoi annulé', 'AbortError')

  const { uploadId } = await httpRequest<{ uploadId: string }>(initPath, {
    method: 'POST',
    body: JSON.stringify({ fileName: file.name, fileSize: file.size, ...target }),
  })

  try {
    for (let offset = 0; offset < file.size; offset += UPLOAD_CHUNK_SIZE) {
      const end = Math.min(offset + UPLOAD_CHUNK_SIZE, file.size)
      await sendUploadChunk(uploadId, file.slice(offset, end), offset, offset, file.size, onProgress, signal)
    }
    return await httpRequest<MediaLibraryUploadResult>(
      `${finishPath}?uploadId=${encodeURIComponent(uploadId)}`,
      { method: 'POST' },
    )
  } catch (err) {
    // Libère le fichier temporaire côté serveur sans bloquer la remontée d'erreur.
    void fetch(`${BASE_URL}/media-library/upload/abort?uploadId=${encodeURIComponent(uploadId)}`, {
      method: 'POST', credentials: 'include', keepalive: true,
    }).catch(() => {})
    throw err
  }
}

export const mediaLibraryApi = {
  listAssets: () => get<{ assets: MediaLibraryAsset[] }>('/media-library/assets'),

  upload: (file: File, destSubPath?: string): Promise<UploadResult> => {
    const form = new FormData()
    form.append('file', file)
    const qs = destSubPath ? `?${new URLSearchParams({ destSubPath }).toString()}` : ''
    return httpRequest<UploadResult>(`/media-library/upload${qs}`, { method: 'POST', body: form })
  },

  createFolder: (name: string, parentSubPath?: string) =>
    httpRequest<{ relativePath: string }>('/media-library/folder', {
      method: 'POST', body: JSON.stringify({ name, parentSubPath }),
    }),

  rename: (subPath: string, newName: string) =>
    httpRequest<{ relativePath: string }>('/media-library/rename', {
      method: 'PATCH', body: JSON.stringify({ subPath, newName }),
    }),

  remove: (subPath: string) =>
    httpRequest<void>('/media-library/file', {
      method: 'DELETE', body: JSON.stringify({ subPath }),
    }),

  /** Dossier annoté : durée et contrôle connus de la base, prochaine diffusion. */
  browse: (subPath?: string) =>
    get<MediaBrowseResult>(`/media-library/browse${toQS({ subPath })}`),

  /** Durées lues à la demande pour les fichiers que la base ne connaît pas. */
  probeDurations: (subPaths: string[]) =>
    httpRequest<{ results: { subPath: string; durationSec: number | null }[] }>('/media-library/probe-durations', {
      method: 'POST', body: JSON.stringify({ subPaths }),
    }),

  /** Fiche complète d'un fichier (analyse + diffusions à venir). */
  details: (subPath: string, version?: string) =>
    get<MediaDetails>(`/media-library/details${toQS({ subPath, v: version })}`),

  /** URL de lecture/aperçu d'un média (lecture seule, Range côté serveur). */
  streamUrl: (subPath: string) =>
    `${BASE_URL}/media-library/stream?${new URLSearchParams({ subPath }).toString()}`,

  /**
   * Envoi chunké, avec progression et annulation. Le fichier part en morceaux
   * courts (init → chunks → finish) : chaque requête reste sous la coupure
   * ~20 s / ~6 Mo qu'impose l'accès distant public (funnel Tailscale), qui
   * tuait un envoi monolithique. `fetch` n'exposant pas la progression d'upload,
   * les morceaux passent par XHR.
   */
  uploadChunked: (
    file: File,
    destSubPath: string | undefined,
    handlers: ChunkedHandlers = {},
  ): Promise<MediaLibraryUploadResult> =>
    sendChunked(file, '/media-library/upload/init', '/media-library/upload/finish', { destSubPath }, handlers),

  /**
   * Remplace un fichier existant (même nom, même extension), par le même envoi
   * chunké. Le serveur refuse si le fichier est à l'antenne ou imminent, à
   * l'ouverture comme à la clôture.
   */
  replaceChunked: (
    file: File,
    targetSubPath: string,
    handlers: ChunkedHandlers = {},
  ): Promise<MediaLibraryUploadResult> =>
    sendChunked(file, '/media-library/replace/init', '/media-library/replace/finish', { targetSubPath }, handlers),
}

// ─── Channels ─────────────────────────────────────────────────────────────────

export const channelsApi = {
  list:    ()          => get<Channel[]>('/channels'),
  getById: (id: string) => get<Channel>(`/channels/${id}`),
}

// ─── Types locaux (résultats API spécifiques) ─────────────────────────────────

export interface ThumbnailCandidate {
  url:    string
  atSec:  number
  origin: 'generated' | 'imported'
}

export interface ProgramThumbnails {
  selectedUrl: string | null
  candidates:  ThumbnailCandidate[]
}

export interface ValidationResult {
  valid:            boolean
  issues:           ValidationIssue[]
  totalItems:       number
  totalDurationSec: number
  coveragePercent:  number
}
export interface ValidationIssue {
  type:     string
  severity: 'ERROR' | 'WARNING'
  itemId:   string
  message:  string
}
/** Réponse de `POST /grids/:id/shift-overlaps` (voir `overlap-shift.service.ts`). */
export interface ShiftOverlapsResult {
  moved:   number
  /** Chevauchements que le recalage laisse en place : déjà à l'antenne, ou doublon. */
  blocked: Array<{ id: string; title: string; reason: 'FROZEN' | 'DUPLICATE' }>
  /** Jingles de liaison posés aux jonctions que le recalage a créées. */
  jingles: number
  /** Jonctions restées sans jingle : le surplus aurait heurté un élément figé ou un doublon. */
  skippedJingles: Array<{ beforeTitle: string; afterTitle: string; blockedTitle: string; reason: 'FROZEN' | 'DUPLICATE' }>
  gridIds: string[]
}
export interface PlaylistExport {
  gridId:     string
  gridName:   string
  channelId:  string
  exportedAt: string
  itemCount:  number
  events:     Record<string, unknown>[]
}
export interface ItemWithConflicts {
  item:         ScheduleItem
  items?:       ScheduleItem[]
  bundleId?:    string | null
  warnings?:     string[]
  conflicts:    unknown[]
  hasConflicts: boolean
}
export interface OkResponse {
  ok:      boolean
  message: string
}

// ─── AI Smart Grid ────────────────────────────────────────────────────────────

export interface SuggestProgramsRequest {
  channelId:           string
  gridId:              string
  date:                string
  customInstructions?: string
  programIds?:         string[]
}

export interface SuggestAdsRequest {
  channelId:           string
  gridId:              string
  date:                string
  customInstructions?: string
  campaignIds?:        string[]
}

export interface SuggestProgramsResponse {
  suggestions:   AiSuggestedProgram[]
  freeSlots:     { startTime: string; endTime: string; durationSec: number }[]
  lockedCount:   number
  suggestionId?: string
  message?:      string
}

export interface SuggestAdsResponse {
  suggestions:   AiSuggestedAd[]
  adBreakCount:  number
  suggestionId?: string
  message?:      string
}

export const aiApi = {
  suggestPrograms: (body: SuggestProgramsRequest) =>
    post<SuggestProgramsResponse>('/ai/suggest-programs', body),

  suggestAds: (body: SuggestAdsRequest) =>
    post<SuggestAdsResponse>('/ai/suggest-ads', body),

  apply: (suggestionId: string, acceptedIndexes: number[]) =>
    post<{ applied: number; total: number }>('/ai/apply', { suggestionId, acceptedIndexes }),

  history: (gridId: string) =>
    get<AiSuggestion[]>(`/ai/history/${gridId}`),
}

// ─── Collections ─────────────────────────────────────────────────────────────

export const collectionsApi = {
  list:   (q: { channelId?: string } = {}) =>
    get<Collection[]>(`/collections${toQS(q)}`),

  getById: (id: string) =>
    get<Collection & { programs: Program[] }>(`/collections/${id}`),

  create: (data: { channelId: string; name: string; description?: string; color?: string }) =>
    post<Collection>('/collections', data),

  update: (id: string, data: Partial<Collection>) =>
    patch<Collection>(`/collections/${id}`, data),

  delete: (id: string) =>
    del<void>(`/collections/${id}`),

  addPrograms: (collectionId: string, programIds: string[]) =>
    post<Collection>(`/collections/${collectionId}/programs`, { programIds }),

  removeProgram: (collectionId: string, programId: string) =>
    del<void>(`/collections/${collectionId}/programs/${programId}`),

  reorder: (collectionId: string, orderedIds: string[]) =>
    patch<{ ok: boolean }>(`/collections/${collectionId}/reorder`, { orderedIds }),
}

// ─── Types de programme (catégories éditoriales) ──────────────────────────────

export const programCategoriesApi = {
  list: (q: { channelId?: string } = {}) =>
    get<ProgramCategory[]>(`/program-categories${toQS(q)}`),

  create: (data: { channelId: string; label: string; color?: string }) =>
    post<ProgramCategory>('/program-categories', data),

  update: (id: string, data: Partial<ProgramCategory>) =>
    patch<ProgramCategory>(`/program-categories/${id}`, data),

  delete: (id: string) =>
    del<void>(`/program-categories/${id}`),

  reorder: (categoryId: string, orderedIds: string[]) =>
    patch<{ ok: boolean }>(`/program-categories/${categoryId}/reorder`, { orderedIds }),
}

// ─── Genres éditoriaux ────────────────────────────────────────────────────────

/** Sous-genre déjà saisi sur des fiches, avec son nombre d'usages. */
export interface SubGenreSuggestion {
  label: string
  count: number
}

export interface ProgramGenreMergeResult {
  merged:   { from: string; into: string }
  programs: number
  series:   number
}

export const programGenresApi = {
  list: (q: { channelId?: string; includeInactive?: boolean } = {}) =>
    get<ProgramGenre[]>(`/program-genres${toQS(q)}`),

  /** Sous-genres déjà saisis, du plus utilisé au moins utilisé. */
  subGenres: (q: { channelId?: string; genreId?: string } = {}) =>
    get<SubGenreSuggestion[]>(`/program-genres/sub-genres${toQS(q)}`),

  create: (data: { channelId: string; label: string; color?: string; sortOrder?: number }) =>
    post<ProgramGenre>('/program-genres', data),

  /**
   * Renommer ne touche aucune fiche : elles référencent la ligne au lieu d'en
   * recopier le libellé, donc le nouveau nom part à l'EPG pour tout le genre.
   */
  update: (id: string, data: Partial<Pick<ProgramGenre, 'label' | 'color' | 'sortOrder' | 'active'>>) =>
    patch<ProgramGenre>(`/program-genres/${id}`, data),

  /**
   * Fusion irréversible : l'appelant doit avoir nommé le nombre de fiches
   * concernées. `subGenre` est écrit sur les fiches déplacées qui n'en portent
   * pas — c'est ce qui absorbe « Sport / Football » dans Sport sans perdre la
   * précision.
   */
  merge: (id: string, data: { targetId: string; subGenre?: string | null }) =>
    post<ProgramGenreMergeResult>(`/program-genres/${id}/merge`, data),

  /** Refusé tant que des fiches le portent : l'interface propose la fusion. */
  delete: (id: string) =>
    del<void>(`/program-genres/${id}`),

  reorder: (genreId: string, orderedIds: string[]) =>
    patch<{ ok: boolean }>(`/program-genres/${genreId}/reorder`, { orderedIds }),
}

// ─── Types d'éléments d'antenne ───────────────────────────────────────────────

export const antennaElementTypesApi = {
  list: (q: { channelId?: string; includeInactive?: boolean } = {}) =>
    get<AntennaElementType[]>(`/antenna-element-types${toQS(q)}`),

  create: (data: { channelId: string; label: string; color?: string; sortOrder?: number }) =>
    post<AntennaElementType>('/antenna-element-types', data),

  update: (id: string, data: Partial<Pick<AntennaElementType, 'label' | 'color' | 'sortOrder' | 'active'>>) =>
    patch<AntennaElementType>(`/antenna-element-types/${id}`, data),

  delete: (id: string) =>
    del<void>(`/antenna-element-types/${id}`),

  reorder: (typeId: string, orderedIds: string[]) =>
    patch<{ ok: boolean }>(`/antenna-element-types/${typeId}/reorder`, { orderedIds }),
}

// ─── Séries ───────────────────────────────────────────────────────────────────

export type SeriesMediaSkipReason = 'NO_CODE_MATCH' | 'INVALID_DATE' | 'DUPLICATE_PART'

export interface SeriesMediaCandidatePart {
  partNumber:   number
  fileName:     string
  absolutePath: string
  relativePath: string
  sizeBytes:    number
  /** Date de dernière modification du fichier sur le disque (ms epoch), pour trier les candidats sans date. */
  mtimeMs:      number | null
  durationSec:  number | null
}

export interface SeriesMediaCandidate {
  /** Identifiant stable du candidat dans le scan — clé à utiliser pour lui associer une date à l'import. */
  groupKey:          string
  date:              string | null  // YYYY-MM-DD, déduit du nom de fichier ; null pour les fichiers à préfixe textuel
  programCode:       string
  parts:             SeriesMediaCandidatePart[]
  totalDurationSec:  number | null
  existingProgramId: string | null
  existingPartCount: number | null
  /** Fiche qui porte déjà ce(s) fichier(s) : réimporter la réutilise. */
  importedProgramId: string | null
  importedTitle:     string | null
  importedArchived:  boolean
  prefix:            string | null  // Préfixe textuel détecté (ex: TEST_SON) ; null pour les fichiers datés
  /** Suffixe descriptif placé après le code programme, utilisé en priorité pour le titre importé. */
  titleHint:         string | null
  /** Saison/épisode lus dans le nom de fichier (`S05_EP01`) ; `null` si le nom n'en porte pas. */
  season:            number | null
  episode:           number | null
}

export interface SeriesMediaScan {
  seriesId:    string
  seriesTitle: string
  baseCode:    string | null
  subPath:     string
  candidates:  SeriesMediaCandidate[]
  ignored:     Array<{ fileName: string; reason: SeriesMediaSkipReason }>
  truncated:   boolean
  /** Numérotation proposée pour la sélection : suite de ce qui existe déjà. */
  suggestedNumbering: { season: number; firstEpisode: number }
  editionCount:       number
  /** `false` : série sans notion de saison/épisode — l'import ne numérote rien. */
  numberEpisodes:     boolean
}

/**
 * À quoi correspond une sélection importée. `null` = import sans numérotation.
 * `SEQUENCE` numérote dans l'ordre de la liste ; `FILENAME` reprend le S/E lu
 * dans chaque nom de fichier, `fallbackSeason` couvrant les noms qui portent
 * l'épisode sans la saison.
 */
export type SeriesImportNumbering =
  | { mode: 'SEQUENCE'; season: number; firstEpisode: number }
  | { mode: 'FILENAME'; fallbackSeason: number }
  | null

export interface SeriesMediaImportResult {
  // `date` vaut `null` pour une édition importée en mode ID (inédit, sans date de diffusion connue).
  created:  Array<{ date: string | null; programId: string; title: string; linkedFiles: number }>
  reused:   Array<{ date: string | null; programId: string; title: string; linkedFiles: number }>
  failed:   Array<{ date: string | null; reason: string }>
  warnings: Array<{ date: string | null; message: string }>
}

export const seriesApi = {
  list:   (q: { channelId?: string; origin?: string; search?: string } = {}) =>
    get<Series[]>(`/series${toQS(q)}`),

  getById: (id: string) =>
    get<Series & { seasons: Record<number, Program[]> }>(`/series/${id}`),

  create: (data: Partial<Series> & { channelId: string; title: string }) =>
    post<Series>('/series', data),

  createEdition: (seriesId: string, data: { plannedFirstAirDate: string | null; durationSec?: number; title?: string; season?: number; episode?: number; partDurationsSec?: number[]; interPartBreakSec?: number }) =>
    post<Program>(`/series/${seriesId}/editions`, data),

  update: (id: string, data: Partial<Series> & { applyGenreToUnclassified?: boolean }) =>
    patch<Series>(`/series/${id}`, data),

  delete: (id: string) =>
    del<void>(`/series/${id}`),

  /** Détection des fichiers portant le code programme de la série dans un dossier. */
  scanMedia: (seriesId: string, subPath = '') =>
    get<SeriesMediaScan>(`/series/${seriesId}/media-scan${toQS({ subPath })}`),

  /**
   * Création en bloc des éditions correspondant aux dates sélectionnées.
   * `numbering` dit à quoi correspond la sélection, et se décide à chaque import.
   * `prefixDates` associe la clé de groupe (`SeriesMediaCandidate.groupKey`) d'un candidat sans date à une date saisie par l'opérateur.
   * `idGroupKeys` sélectionne des candidats sans date importés « inédit » (code programme en mode ID, sans date fictive).
   */
  importMedia: (
    seriesId: string,
    subPath: string,
    dates: string[],
    numbering: SeriesImportNumbering,
    prefixDates?: Record<string, string> | null,
    idGroupKeys?: string[] | null,
  ) =>
    post<SeriesMediaImportResult>(`/series/${seriesId}/media-import`, { subPath, dates, numbering, prefixDates, idGroupKeys }),

  attachProgram: (seriesId: string, programId: string, season?: number, episode?: number) =>
    post<Program>(`/series/${seriesId}/programs/${programId}`, { season, episode }),

  detachProgram: (seriesId: string, programId: string) =>
    del<void>(`/series/${seriesId}/programs/${programId}`),

  // ── Miniature ──────────────────────────────────────────────────────────
  thumbnailFromMedia: (seriesId: string, filePath: string) =>
    post<{ thumbnailUrl: string }>(`/series/${seriesId}/thumbnail/from-media`, { filePath }),
  importThumbnail: async (seriesId: string, file: File): Promise<{ thumbnailUrl: string }> => {
    const body = new FormData()
    body.append('file', file)
    // FormData porte sa propre frontière multipart : surtout ne pas fixer
    // Content-Type à la main, le navigateur doit l'écrire.
    return httpRequest<{ thumbnailUrl: string }>(`/series/${seriesId}/thumbnail/import`, { method: 'POST', body })
  },
  clearThumbnail: (seriesId: string) =>
    del<void>(`/series/${seriesId}/thumbnail`),
}

// ─── Query types ──────────────────────────────────────────────────────────────

export interface ProgramQuery {
  channelId?:    string
  catalog?:      'programs' | 'jingles'
  type?:         string
  search?:       string
  tags?:         string
  isNew?:        'true' | 'false'
  isLive?:       'true' | 'false'
  collectionId?: string
  seriesId?:     string
  programCategoryId?: string
  /** Genre éditorial (liste fermée), l'axe publié à l'EPG. */
  programGenreId?: string
  /**
   * Facette « État » : `incomplete` reprend la règle « Prêt pour l'antenne »
   * (titre, titre EPG, genre), `genreless` isole les fiches sans rubrique, et
   * `incomplete-today` restreint « à compléter » à ce qui est posé sur la
   * journée d'antenne en cours.
   */
  readiness?:    'incomplete' | 'genreless' | 'incomplete-today'
  /** Facette « Média » : fichier présent, aucun média, ou absent du disque. */
  mediaState?:   'available' | 'none' | 'missing-file'
  /** Inclure aussi les directs ad-hoc (cachés du catalogue par défaut). */
  includeAdHoc?: 'true' | 'false'
  /** N'inclure QUE les directs ad-hoc. */
  onlyAdHoc?:    'true' | 'false'
  /** Filtre archivage : 'active' (défaut), 'archived', 'all'. */
  archived?:     'active' | 'archived' | 'all'
  /**
   * Découpage autour d'aujourd'hui (date de première diffusion prévue) :
   * `upcoming` = demain et plus loin, `current` = le reste. Le catalogue
   * charge `current` et garde `upcoming` derrière sa bande repliable.
   */
  premiere?:     'upcoming' | 'current'
  page?:         number
  limit?:        number
  /** `premiereDate` : date de première diffusion (prévue, sinon constatée). */
  sortBy?:       'title' | 'createdAt' | 'durationSec' | 'type' | 'premiereDate'
  sortDir?:      'asc' | 'desc'
}

// ─── Santé des données ────────────────────────────────────────────────────────

export const dataHealthApi = {
  summary: (channelId?: string) =>
    get<DataHealthSummary>(`/data-health${toQS({ channelId })}`),

  entries: (q: {
    channelId?:     string
    family?:        AssetFamily
    status?:        AssetPresenceStatus
    purgeableOnly?: boolean
  } = {}) => get<AssetPresenceEntry[]>(`/data-health/entries${toQS(q)}`),

  /** Vérification immédiate, sans attendre le tick de 5 minutes. */
  scan: () => post<AssetPresenceScanReport>('/data-health/scan'),

  /** Le serveur revalide chaque identifiant : une liste périmée est refusée ligne à ligne. */
  purge: (ids: string[]) => post<AssetPurgeResult>('/data-health/purge', { ids }),
}
